// Floor catalogue sync + offline cache (Module 01 S9, §5 floor-cache / §9.14).
// Wraps the generated api-client (doctrine 3) to pull the facility's floor-cache
// delta, fold it into the persisted snapshot (tombstones + cursor progression),
// and expose an offline-first view: the card always renders from the best
// available snapshot — a fresh sync when online, the last persisted copy when
// not — with staleness surfaced to the banner. Cost never appears here (the
// endpoint strips it unconditionally, §7/§13.4).

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { client } from "../../../lib/api";
import { RequestError, unwrap } from "../../../lib/request";
import {
  loadSnapshot,
  mergeFloorDelta,
  saveSnapshot,
  type FloorSnapshot,
} from "../lib/floor-cache-store";
import { useOnline } from "./use-online";

export interface FloorCatalogueResult {
  /** Best available snapshot: fresh sync, else last persisted, else undefined. */
  snapshot: FloorSnapshot | undefined;
  syncedAt: number | undefined;
  /** A sync request is in flight. */
  isSyncing: boolean;
  /** First load with nothing cached yet — render a skeleton. */
  isInitialLoading: boolean;
  /** Showing a cached snapshot because this session's sync did not succeed. */
  isStale: boolean;
  isOffline: boolean;
  /** A structured sync failure (request id for the error card), if any. */
  syncError: RequestError | undefined;
  refresh: () => void;
}

export function useFloorCatalogue(facilityId: string | undefined): FloorCatalogueResult {
  const online = useOnline();

  const query = useQuery({
    queryKey: ["floor", "floor-cache", facilityId ?? null],
    enabled: facilityId !== undefined && facilityId.length > 0,
    retry: false,
    // Attempt even when the browser reports offline (flaky signal); a real
    // failure falls back to the persisted snapshot below.
    networkMode: "always",
    queryFn: async ({ signal }): Promise<FloorSnapshot> => {
      if (facilityId === undefined) throw new Error("facility required");
      // loadSnapshot only returns the snapshot stored under this facility, so
      // its cursor is always the right one to hand back.
      const prior = loadSnapshot(facilityId);
      const since = prior?.since;
      const data = unwrap(
        await client.GET("/v1/catalogue/floor-cache", {
          signal,
          params: {
            query:
              since !== undefined
                ? { facility_id: facilityId, since }
                : { facility_id: facilityId },
          },
        }),
        "floor-cache",
      );
      const merged = mergeFloorDelta(prior, data, facilityId, Date.now());
      saveSnapshot(merged);
      return merged;
    },
  });

  // Re-read the persisted fallback whenever the query result changes, so an
  // offline/error render still has the last good snapshot to draw the card from.
  const persisted = useMemo(
    () => loadSnapshot(facilityId),
    [facilityId, query.dataUpdatedAt, query.errorUpdatedAt],
  );

  const snapshot = query.data ?? persisted;
  const syncError = query.error instanceof RequestError ? query.error : undefined;

  return {
    snapshot,
    syncedAt: snapshot?.syncedAt,
    isSyncing: query.isFetching,
    isInitialLoading: query.isFetching && snapshot === undefined,
    isStale: !query.isSuccess && snapshot !== undefined,
    isOffline: !online,
    syncError,
    refresh: () => {
      void query.refetch();
    },
  };
}
